import type { RagResultToolDef } from '@kanbu/schema/contracts';
import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { holdBackIncompleteLink } from './markdown-streaming';
import { SourceChip } from '../components/sources/source-chip';
import {
  buildCitationNumbering,
  preprocessCitationMarkers,
  stripCitationMarkers,
  type CitationEntry,
} from '../components/sources/source-utils';

const baseComponents: Partial<Components> = {
  pre: ({ children }) => (
    <pre className='bg-muted text-xs bg-chat-background border overflow-auto whitespace-break-spaces w-full p-2 rounded-lg [overflow-wrap:anywhere]'>
      {children}
    </pre>
  ),
  img: ({ node, children, ...props }) => {
    return (
      <img
        className='w-full border border-chat-border rounded-md my-3'
        {...props}
      />
    );
  },
  p: ({ node, children, ...props }) => {
    return (
      <p className='my-1' {...props}>
        {children}
      </p>
    );
  },
  ol: ({ node, children, ...props }) => {
    return (
      <ol
        className='list-decimal my-1 list-outside ml-8 [&_ul]:ml-3 [&_ol]:ml-3'
        {...props}
      >
        {children}
      </ol>
    );
  },
  li: ({ node, children, ...props }) => {
    return (
      <li className='py-0.5' {...props}>
        {children}
      </li>
    );
  },
  ul: ({ node, children, ...props }) => {
    return (
      <ul
        className='list-decimal my-1 list-outside ml-8 [&_ul]:ml-3 [&_ol]:ml-3'
        {...props}
      >
        {children}
      </ul>
    );
  },
  strong: ({ node, children, ...props }) => {
    return (
      <span className='font-semibold' {...props}>
        {children}
      </span>
    );
  },
  a: ({ node, children, ...props }) => {
    return (
      <a className='underline' target='_blank' rel='noreferrer' {...props}>
        {children}
      </a>
    );
  },
  h1: ({ node, children, ...props }) => {
    return (
      <h1 className='text-3xl font-bold mt-4 mb-2' {...props}>
        {children}
      </h1>
    );
  },
  h2: ({ node, children, ...props }) => {
    return (
      <h2 className='text-2xl font-semibold mt-4 mb-2' {...props}>
        {children}
      </h2>
    );
  },
  h3: ({ node, children, ...props }) => {
    return (
      <h3 className='text-xl font-semibold mt-4 mb-2' {...props}>
        {children}
      </h3>
    );
  },
  h4: ({ node, children, ...props }) => {
    return (
      <h4 className='text-lg font-semibold mt-4 mb-2' {...props}>
        {children}
      </h4>
    );
  },
  h5: ({ node, children, ...props }) => {
    return (
      <h5 className='text-base font-semibold mt-4 mb-2' {...props}>
        {children}
      </h5>
    );
  },
  h6: ({ node, children, ...props }) => {
    return (
      <h6 className='text-sm font-semibold mt-4 mb-2' {...props}>
        {children}
      </h6>
    );
  },
  table: ({ node, children, ...props }) => {
    return (
      <div className='overflow-x-auto my-4 max-w-full [overflow-wrap:normal]'>
        <table
          className='w-full border-collapse border border-chat-border rounded-md'
          {...props}
        >
          {children}
        </table>
      </div>
    );
  },
  thead: ({ node, children, ...props }) => {
    return (
      <thead className='bg-muted' {...props}>
        {children}
      </thead>
    );
  },
  tbody: ({ node, children, ...props }) => {
    return (
      <tbody className='divide-y divide-border' {...props}>
        {children}
      </tbody>
    );
  },
  tr: ({ node, children, ...props }) => {
    return (
      <tr className='hover:bg-muted/50' {...props}>
        {children}
      </tr>
    );
  },
  th: ({ node, children, ...props }) => {
    return (
      <th
        className='px-4 py-2 text-left font-semibold border-b border-chat-border'
        {...props}
      >
        {children}
      </th>
    );
  },
  td: ({ node, children, ...props }) => {
    return (
      <td
        className='px-4 py-2 border-r last:border-r-0 border-chat-border'
        {...props}
      >
        {children}
      </td>
    );
  },
  u: ({ node, children, ...props }) => {
    return (
      <span className='underline' {...props}>
        {children}
      </span>
    );
  },
};

function buildComponents(
  numbering: Map<string, CitationEntry>,
): Partial<Components> {
  return {
    ...baseComponents,
    sup: ({ node, children, ...props }) => {
      const citeId =
        (node?.properties?.['data-cite'] as string | undefined) ??
        (props as { 'data-cite'?: string })['data-cite'];

      // Not one of our injected citation markers — preserve real superscripts.
      if (!citeId) {
        return <sup {...props}>{children}</sup>;
      }

      const entry = numbering.get(citeId.trim());

      // Unknown / stripped id — render nothing.
      if (!entry) {
        return null;
      }

      return (
        <SourceChip
          source={entry.source}
          number={entry.number}
          variant='superscript'
        />
      );
    },
  };
}

/**
 * `rehypeRaw` turns literal HTML embedded in the model's markdown output
 * into real DOM nodes instead of react-markdown's normal inert-text
 * handling — necessary for the `<sup data-cite>` citation markers this
 * file injects itself (see `preprocessCitationMarkers`), and for models
 * that reply with raw HTML formatting. Without a sanitizer after it, that
 * is a direct stored-XSS path: this app's own threat model already treats
 * retrieved web/tool content as attacker-reachable, and a model induced to
 * echo `<img src=x onerror=...>` would render it as live HTML for every
 * visitor in the thread. `rehypeSanitize` strips anything dangerous from
 * the tree `rehypeRaw` just built, extending GitHub's default allowlist
 * only for the two things this file actually relies on: `data-cite` on
 * `<sup>` (the citation markers above) and the `<u>` tag (underline,
 * rendered by `baseComponents.u` below — not in GFM, so only reachable via
 * raw HTML, same as the citation markers).
 */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  attributes: {
    ...defaultSchema.attributes,
    sup: [...(defaultSchema.attributes?.sup ?? []), 'dataCite'],
  },
};

const remarkPlugins = [remarkGfm];
const rehypePlugins: NonNullable<Options['rehypePlugins']> = [
  rehypeRaw,
  [rehypeSanitize, sanitizeSchema],
];

export interface MarkdownProps {
  children: string;
  citations?: RagResultToolDef['sources'] | null;
  /**
   * Render `[cN]` markers as inline numeric superscript chips. Default off:
   * provenance is shown as a sources list at the end of the answer and the
   * inline markers are stripped for clean prose.
   */
  inlineCitations?: boolean;
  /**
   * The text is still being streamed: a link that is only half written is
   * held back until it is complete instead of showing as raw `[label](url`.
   */
  streaming?: boolean;
}

function NonMemoizedMarkdown({
  children,
  citations,
  inlineCitations = false,
  streaming = false,
}: MarkdownProps) {
  /**
   * Numbering is derived from the text alone, so inline markers render as
   * numeric superscripts immediately while streaming (before `sources`
   * arrive) and hydrate into links once they do.
   */
  const numbering = useMemo(
    () =>
      inlineCitations ? buildCitationNumbering(children, citations) : null,
    [children, citations, inlineCitations],
  );

  const processed = useMemo(() => {
    const text = streaming ? holdBackIncompleteLink(children) : children;

    if (inlineCitations) {
      return numbering && numbering.size > 0
        ? preprocessCitationMarkers(text)
        : text;
    }

    // List-mode default: drop inline markers for clean prose.
    return CITATION_MARKER_TEST.test(text) ? stripCitationMarkers(text) : text;
  }, [children, numbering, inlineCitations, streaming]);

  const components = useMemo(
    () => (numbering ? buildComponents(numbering) : baseComponents),
    [numbering],
  );

  return (
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {processed}
    </ReactMarkdown>
  );
}

const CITATION_MARKER_TEST = /\[c\d+/i;

export const Markdown = memo(
  NonMemoizedMarkdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.citations === nextProps.citations &&
    prevProps.inlineCitations === nextProps.inlineCitations &&
    prevProps.streaming === nextProps.streaming,
);
